import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { APIError, getAPIErrorCode } from '@vizit/auth';
import type { BenchmarkSortFields, BenchmarkView as GeneratedBenchmarkView, ImageType } from '../../generated';
import type { SortDirection } from '../../generated';
import { searchBenchmarksV1WorkspacesWorkspaceIdBenchmarksGet } from '../../generated';
import { extractTotalCount } from '../../utils';

/*
 * GET /v1/workspaces/{workspace_id}/benchmarks: the workspace's custom benchmarks, with the total in
 * `Vizit-Total-Count`. `toBenchmarkView` is the one place the generated `BenchmarkView` is narrowed for callers.
 * Tests answer this path with the MSW handler in `tests/mocks/handlers.ts`.
 */

/**
 * The route pages at 10 by default and sets no maximum. Every caller wants the whole list (the Benchmarks view has no
 * paging, the create-collection dialog offers every benchmark), so one page well above any workspace's count.
 */
export const BENCHMARK_LIST_LIMIT = 500;

export type BenchmarkSortBy = BenchmarkSortFields;

export type BenchmarkSortDirection = SortDirection;

/**
 * A custom benchmark as the list and create routes return it: the generated `BenchmarkView` with `scope` narrowed to
 * an image type (the spec types it as a plain string; the API only stores hero or carousel) and `name` never `null`.
 */
export type BenchmarkView = Omit<GeneratedBenchmarkView, 'name' | 'scope'> & { name: string; scope: ImageType };

export function toBenchmarkView(benchmark: GeneratedBenchmarkView): BenchmarkView {
  return { ...benchmark, name: benchmark.name ?? '', scope: benchmark.scope as ImageType };
}

export interface SearchBenchmarksQueryVariables {
  workspace_id: string;
  name_like?: string;
  product_category_id?: string;
  /** Only benchmarks of this image type; omit for every scope. */
  scope?: ImageType;
  sort_by?: BenchmarkSortBy;
  sort_direction?: BenchmarkSortDirection;
}

export type SearchBenchmarksQuery = {
  data: BenchmarkView[];
  /** Every benchmark matching the request, from `Vizit-Total-Count`, which can exceed the page in `data`. */
  total_count: number;
};

async function searchBenchmarks({
  workspace_id,
  ...query
}: SearchBenchmarksQueryVariables): Promise<SearchBenchmarksQuery> {
  const { data, response } = await searchBenchmarksV1WorkspacesWorkspaceIdBenchmarksGet({
    path: { workspace_id },
    query: { limit: BENCHMARK_LIST_LIMIT, ...query },
    throwOnError: true,
  });
  const benchmarks = (data ?? []).map(toBenchmarkView);
  return { data: benchmarks, total_count: extractTotalCount(response, benchmarks.length) };
}

export const useSearchBenchmarksQuery = <TData = SearchBenchmarksQuery, TError = unknown>(
  variables: SearchBenchmarksQueryVariables,
  options?: Omit<UseQueryOptions<SearchBenchmarksQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchBenchmarksQuery, TError, TData>({
    queryKey: useSearchBenchmarksQuery.getKey(variables),
    queryFn: () => searchBenchmarks(variables),
    ...options,
  });
};

useSearchBenchmarksQuery.getKey = (variables: SearchBenchmarksQueryVariables) => ['SearchBenchmarks', variables];

useSearchBenchmarksQuery.fetcher = (variables: SearchBenchmarksQueryVariables) => () => searchBenchmarks(variables);

/**
 * The benchmark routes answer 404 `NOT_FOUND` for an organisation that does not
 * have custom benchmarks enabled. Other 404s (an unknown workspace answers
 * `WORKSPACE_NOT_FOUND`, a route the API does not have answers no code) are
 * failed requests.
 */
export function isBenchmarksUnavailableError(error: unknown) {
  return error instanceof APIError && error.status === 404 && getAPIErrorCode(error) === 'NOT_FOUND';
}
